import { Tabs, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import PaypalAccount from "./PaypalAccount"
import PowerTranzAccount from "./PowerTranzAccount"
import styles from "./index.module.css"
import { StripeAccount } from "../../StripeAccount"
import { PAYPAL_TAB, POWERTRANZ_TAB, STRIPE_TAB } from "./constants"
import { isFeatureEnabled } from "config/features"
import { getPowerTranzAccountStatus } from "store/payment-provider"
import { isPowerTranzAvailableSelector } from "store/user"
import { Paragraph } from "ui/Typography"

const TAB_KEYS = { [STRIPE_TAB]: "1", [PAYPAL_TAB]: "2", [POWERTRANZ_TAB]: "3" }

const PaymentProviders = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams("")
  const [activeKey, setActiveKey] = useState("1")
  const dispatch = useDispatch()
  // The API decides whether PowerTranz exists: GET status answers 404 while its switch is off, and
  // the tab stays hidden so this screen is exactly as before (integration guide 1, 4).
  const isPowerTranzAvailable = useSelector(isPowerTranzAvailableSelector)

  useEffect(() => {
    dispatch(getPowerTranzAccountStatus()).catch(() => {})
  }, [dispatch])

  const isPowerTranzTabVisible = isPowerTranzAvailable

  useEffect(() => {
    const tabParam = searchParams.get("tab")
    if (tabParam === PAYPAL_TAB && isFeatureEnabled('PAYPAL_SUPPORT')) {
      setActiveKey(TAB_KEYS[PAYPAL_TAB])
    } else if (tabParam === POWERTRANZ_TAB && isPowerTranzTabVisible) {
      setActiveKey(TAB_KEYS[POWERTRANZ_TAB])
    } else {
      setActiveKey(TAB_KEYS[STRIPE_TAB])
    }
  }, [searchParams, isPowerTranzTabVisible])

  const handleTabChange = (key) => {
    setActiveKey(key)
    const tab = Object.keys(TAB_KEYS).find((tabName) => TAB_KEYS[tabName] === key) || STRIPE_TAB
    setSearchParams({ tab })
  }

  const items = [
    {
      key: TAB_KEYS[STRIPE_TAB],
      label: t("guide.settings.stripe"),
      children: <StripeAccount />
    },
    ...(isFeatureEnabled('PAYPAL_SUPPORT') ? [{
      key: TAB_KEYS[PAYPAL_TAB],
      label: t("pro.settings.paypal.text"),
      children: <PaypalAccount />
    }] : []),
    ...(isPowerTranzTabVisible
      ? [
          {
            key: TAB_KEYS[POWERTRANZ_TAB],
            label: t("powertranz.name"),
            // Unmount when another tab is shown so typed credentials don't linger in a hidden form.
            destroyInactiveTabPane: true,
            children: <PowerTranzAccount />
          }
        ]
      : [])
  ]

  return (
    <Flex vertical>
      <Paragraph size="md">
        {t(isPowerTranzTabVisible ? "pro.settings.paymentProviders.description_with_powertranz" : "pro.settings.paymentProviders.description")}
      </Paragraph>
      <Tabs className={styles.paymentProvidersTabs} activeKey={activeKey} items={items} onChange={handleTabChange} />
    </Flex>
  )
}

export default PaymentProviders
